import { memo, useState, useCallback, useEffect, useMemo, useContext } from 'react';

import { SectionForm } from '@Sage/hoi-poi-ui';
import { dot } from '@Sage/ssm-utils';

import SelectHoi from 'components/SelectHoi';
import { getAutomationVariables } from 'services/AutomationsService';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { errorToast } from 'utils/toast';

import {
    buildPathTree,
    canConfigureMailingListActions,
    filterAutomationActionsByAvailability,
    findAutomationAction,
    isMailingListAction,
} from '../../../../utils';
import { AutomationBuilderCanvasContext } from '../../AutomationBuilderCanvasContext';
import AutomationBuilderSidePanel from '../AutomationBuilderSidePanel';
import MailingListAction from './actions/MailingListAction';
import UpdateEntityRelatedAction from './actions/UpdateEntityRelatedAction';

const ACTION_MAP = {
    update_entity_related: UpdateEntityRelatedAction,
    create_entity: UpdateEntityRelatedAction,
    mailing_list_add: MailingListAction,
    mailing_list_remove: MailingListAction,
};

const AutomationBuilderActionSidePanel = memo(
    ({ node, data, nodePath, onChange, onClose, isTouched, setTouched }) => {
        const [entities, setEntities] = useState([]);
        const { getActions, dispatch } = useContext(AutomationBuilderCanvasContext);
        const [actions, setActions] = useState();
        const [form, setForm] = useState(() => {
            let newNode = JSON.parse(JSON.stringify(node));
            const actionComponent = ACTION_MAP[newNode.name];
            if (actionComponent) newNode = actionComponent.getInitialData(newNode, data);
            return newNode;
        });
        const [variables, setVariables] = useState();

        const getVariablesData = useCallback(
            (actionName) => {
                let finalData = data;
                if (actionName) {
                    finalData = dot.set(data, `${nodePath}.name`, actionName);
                }

                const nodeBranch = buildPathTree(finalData, nodePath);
                getAutomationVariables(nodeBranch?.scene)
                    .then((result) => {
                        setEntities(
                            (result.entity || []).map((entity) => ({
                                label: getLiteral(entity.name),
                                options: (entity.fields || []).map((field) => ({
                                    ...field,
                                    label: getLiteralWithParameters(field.literal, [
                                        getLiteral(data?.scene?.parameters?.entity?.description),
                                    ]),
                                    value: field.value,
                                    iconType: field?.entity,
                                    entity: field?.entity,
                                    isDisabled: Boolean(field.isDisabled),
                                    hint: field.isDisabled
                                        ? {
                                              title: getLiteral(
                                                  'label_automations_entity_integrated_sage',
                                              ),
                                              body: getLiteral(
                                                  'label_automations_entity_integrated_sage_desc',
                                              ),
                                          }
                                        : undefined,
                                })),
                            })),
                        );

                        setVariables(result);
                    })
                    .catch((e) => {
                        console.error(e);
                        errorToast({ text: getLiteral('error_generalerror') });
                    });
            },
            [data, nodePath],
        );

        useEffect(() => {
            // Getting actions and select lists when updating
            if (actions?.length > 0) return;

            getActions().then((result) => {
                setActions(result);
            });

            if (variables) return;
            getVariablesData();
        }, [actions?.length, getActions, getVariablesData, variables]);

        const changeEvent = useCallback(
            (value) => {
                setTouched(true);
                setForm(() => {
                    const newObj = {
                        name: value,
                        parameters: {},
                    };
                    return newObj;
                });

                getVariablesData(value);
            },
            [getVariablesData, setTouched],
        );

        const changeEntity = useCallback(
            (value) => {
                setTouched(true);
                setForm((current) => {
                    const newObj = {
                        ...current,
                        parameters: {
                            ...current.parameters,
                            entity: value,
                        },
                        fields: [],
                        values: {},
                        operators: {},
                    };
                    return newObj;
                });

                if (!value) return;
            },
            [setTouched],
        );

        const onChangeAction = useCallback(
            (cb, touched = true) => {
                if (touched) setTouched(true);
                setForm(cb);
            },
            [setTouched],
        );

        const mailingListAvailable = useMemo(
            () =>
                canConfigureMailingListActions({
                    triggerEntity: data?.scene?.parameters?.entity?.value,
                    entityGroups: entities,
                    variablesLoaded: Boolean(variables),
                }),
            [data?.scene?.parameters?.entity?.value, entities, variables],
        );

        const visibleActions = useMemo(
            () =>
                filterAutomationActionsByAvailability(actions || [], {
                    mailingListAvailable,
                    selectedAction: form.name,
                }),
            [actions, form.name, mailingListAvailable],
        );

        const onDone = useCallback(() => {
            const actionComponent = ACTION_MAP[form?.name];
            if (!actionComponent) return;
            const savedData = actionComponent.getSaveData(form);
            onChange(nodePath, {
                id: node.id,
                position: node.position,
                name: form.name,
                parameters: savedData,
                type: 'action',
                _isIncomplete: false,
                next: node.next,
            });

            // Updating selected node if node name changes
            if (node.name !== form.name) {
                dispatch({ type: 'select', node: { ...node, ...form, parameters: savedData } });
            }

            setTouched(false);
        }, [onChange, nodePath, form, node, setTouched, dispatch]);

        const isDoneDisabled = useMemo(() => {
            if (!form?.name) return true;
            if (!isTouched) return true;
            if (!ACTION_MAP[form?.name]) return true;
            if (isMailingListAction(form.name) && !mailingListAvailable) return true;
            return ACTION_MAP[form?.name].hasEmptyFields?.(form);
        }, [form, isTouched, mailingListAvailable]);

        const ActionComponent = useMemo(() => ACTION_MAP[form?.name] || null, [form.name]);

        return (
            <AutomationBuilderSidePanel
                title={`${getLiteral('label_action')} ${node?.position}`}
                subtitle={getLiteral('label_action_desc')}
                onClose={onClose}
                isDoneDisabled={isDoneDisabled}
                onDone={onDone}
            >
                <SectionForm title={getLiteral('label_action')} isExpandable={false}>
                    <SelectHoi
                        label={getLiteral('label_action_event')}
                        placeholder={getLiteral('label_placeholder_event')}
                        options={visibleActions}
                        onChange={changeEvent}
                        value={form.name}
                        useMenuPortal={false}
                        isFullWidth
                        usePlainValue
                    />
                    <SelectHoi
                        label={getLiteral('label_action_entity')}
                        placeholder={getLiteral('label_placeholder_entity')}
                        options={entities}
                        onChange={changeEntity}
                        value={form.parameters?.entity}
                        useMenuPortal={false}
                        isReadOnly={!form.name}
                        isFullWidth
                    />
                </SectionForm>
                {ActionComponent && (
                    <ActionComponent
                        key={form.parameters?.entity?.value}
                        onChange={onChangeAction}
                        data={data}
                        form={form}
                        variables={variables}
                        action={findAutomationAction(visibleActions, form.name)}
                    />
                )}
            </AutomationBuilderSidePanel>
        );
    },
);

export default AutomationBuilderActionSidePanel;
